import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  AiVoice,
  AiVoiceScript,
  AiVoiceScriptCreate,
  AiVoiceScriptUpdate,
} from 'types/ai-voice';
import {
  deleteAiVoiceScript,
  getAiVoice,
  getAiVoiceScripts,
  postAiVoiceScript,
  putAiVoiceScript,
} from 'shared/api';
import { AppState } from './reducers';

// STATE
export const getInitialState = () => ({
  aiVoice: {
    loaded: false,
    loading: false,
    error: '',
    data: null as AiVoice | null,
  },
  aiVoiceScripts: {
    loaded: false,
    loading: false,
    error: '',
    data: [] as AiVoiceScript[],
    creating: false,
    creatingError: '',
    updating: false,
    updatingError: '',
  },
});

const initialState = getInitialState();
export type AiVoiceState = typeof initialState;

export const loadAiVoice = createAsyncThunk<AiVoice, void, { state: AppState }>(
  'ai-voice/loadAiVoice',
  async () => {
    const res = await getAiVoice();
    return res.data;
  },
);

export const loadAiVoiceScripts = createAsyncThunk<
  AiVoiceScript[],
  void,
  { state: AppState }
>('ai-voice/loadAiVoiceScripts', async () => {
  const res = await getAiVoiceScripts();
  return res.data;
});

export const updateAiVoiceScript = createAsyncThunk<
  AiVoiceScript,
  AiVoiceScriptUpdate,
  { state: AppState }
>('ai-voice/updateAiVoiceScript', async aiVoiceScript => {
  if (aiVoiceScript.id) {
    const res = await putAiVoiceScript(aiVoiceScript);
    return res.data;
  } else {
    throw new Error('No id provided to updateAiVoiceScript');
  }
});

export const createAiVoiceScript = createAsyncThunk<
  AiVoiceScript,
  AiVoiceScriptCreate,
  { state: AppState }
>('ai-voice/createAiVoiceScript', async aiVoiceScript => {
  const res = await postAiVoiceScript(aiVoiceScript);
  return res.data;
});

export const removeAiVoiceScript = createAsyncThunk<
  void,
  AiVoiceScript['id'],
  { state: AppState }
>('ai-voice/removeAiVoiceScript', async aiVoiceScriptId => {
  await deleteAiVoiceScript({ id: aiVoiceScriptId });
});

const aiVoiceSlice = createSlice({
  name: 'ai-voice',
  initialState: getInitialState(),
  reducers: {
    resetAiVoiceState: () => getInitialState(),
  },
  extraReducers: builder => {
    builder
      .addCase(loadAiVoice.pending, state => {
        state.aiVoice.loading = true;
        state.aiVoice.error = '';
      })
      .addCase(loadAiVoice.fulfilled, (state, action) => {
        state.aiVoice.loaded = true;
        state.aiVoice.loading = false;
        state.aiVoice.data = action.payload;
      })
      .addCase(loadAiVoice.rejected, (state, action) => {
        state.aiVoice.loaded = true;
        state.aiVoice.loading = false;
        state.aiVoice.data = null;
        state.aiVoice.error = action.error.message || 'Error loading AI Voice.';
      });

    builder
      .addCase(loadAiVoiceScripts.pending, state => {
        state.aiVoiceScripts.loading = true;
        state.aiVoiceScripts.error = '';
      })
      .addCase(loadAiVoiceScripts.fulfilled, (state, action) => {
        state.aiVoiceScripts.loaded = true;
        state.aiVoiceScripts.loading = false;
        state.aiVoiceScripts.data = action.payload;
      })
      .addCase(loadAiVoiceScripts.rejected, (state, action) => {
        state.aiVoiceScripts.loaded = true;
        state.aiVoiceScripts.loading = false;
        state.aiVoiceScripts.data = [];
        state.aiVoiceScripts.error =
          action.error.message || 'Error loading AI Voice Scripts.';
      });

    builder
      .addCase(createAiVoiceScript.pending, state => {
        state.aiVoiceScripts.creating = true;
        state.aiVoiceScripts.creatingError = '';
      })
      .addCase(createAiVoiceScript.fulfilled, (state, action) => {
        state.aiVoiceScripts.creating = false;
        state.aiVoiceScripts.data = [action.payload, ...state.aiVoiceScripts.data];
      })
      .addCase(createAiVoiceScript.rejected, (state, action) => {
        state.aiVoiceScripts.creating = false;
        state.aiVoiceScripts.creatingError =
          action.error.message || 'Error creating AI Voice Script.';
      });

    builder
      .addCase(updateAiVoiceScript.pending, state => {
        state.aiVoiceScripts.updating = true;
        state.aiVoiceScripts.updatingError = '';
      })
      .addCase(updateAiVoiceScript.fulfilled, (state, action) => {
        state.aiVoiceScripts.updating = false;
        const withoutExisting = state.aiVoiceScripts.data.filter(
          s => s.id !== action.payload.id,
        );
        state.aiVoiceScripts.data = [action.payload, ...withoutExisting];
      })
      .addCase(updateAiVoiceScript.rejected, (state, action) => {
        state.aiVoiceScripts.updating = false;
        state.aiVoiceScripts.updatingError =
          action.error.message || 'Error creating AI Voice Script.';
      });

    builder.addCase(removeAiVoiceScript.fulfilled, (state, action) => {
      state.aiVoiceScripts.data = state.aiVoiceScripts.data.filter(
        s => s.id !== action.meta.arg,
      );
    });
  },
});

export const selectAiVoiceScriptById = createSelector(
  [
    (state: AppState) => state.aiVoice.aiVoiceScripts.data,
    (_: AppState, id: AiVoiceScript['id']) => id,
  ],
  (list, id) => list.find(s => s.id === id),
);

export const { resetAiVoiceState } = aiVoiceSlice.actions;

export default aiVoiceSlice.reducer;
